import React, { useRef } from 'react';
import PageTitle from '@components/pageTitle';
import translate from '@data/translations';
import { ReactComponent as Logo } from '../../assets/custom/logo/logo.svg';
import useStore from '@data/state/zustand';
import InlineModal from '@components/modalInline';
import { createTrelloCard } from './api';
import ConfirmButtons from '@components/buttons/confirmButtons';
import { BlobDocumentModel } from '@common/modelDefinition';
import FolderBox from '@components/documentTools/folderBoxV2';
import { checkValidationInputs } from '@components/validationRefs';
import { toast } from 'react-toastify';

interface AboutPageProps {
    history: any;
}
export const openInNewTab = (url: string) => {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (newWindow) newWindow.opener = null;
};

export interface CustomerSupportForm {
    facility: string;
    description: string;
    email: string;
    documentUuids: string[];
}

function HelpAndSupport(props: AboutPageProps) {
    const codeVersion = useStore((appState: any) => appState.version);
    const [customerSupport, setCustomerSupport] = React.useState(null as CustomerSupportForm | null);
    const [attachedDocuments, setAttachedDocuments] = React.useState([] as BlobDocumentModel[]);
    const emailRef = useRef(null);

    React.useEffect(() => {
        const loadData = async () => {
            checkValidationInputs(customerSupport, getValidationInputs);
        };

        loadData().catch((err) => {
            console.log(err);
        });
        // eslint-disable-next-line
    }, []);

    const handleCustomerInputs = (e: any) => {
        if (customerSupport) {
            const newCustomerSupport = { ...customerSupport };
            newCustomerSupport[e.target.name as keyof CustomerSupportForm] = e.target.value;
            setCustomerSupport(newCustomerSupport);
            checkValidationInputs(newCustomerSupport, getValidationInputs);
        }
    };

    const getValidationInputs = (data: CustomerSupportForm) => {
        let invalidInputs = [];
        let validInputs = [];

        if (data.email && data.email !== null && data.email.trim() !== '') {
            /* eslint-disable-next-line */
            const re =
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            if (!re.test(String(data.email).toLowerCase())) {
                invalidInputs.push(emailRef);
            } else {
                validInputs.push(emailRef);
            }
        } else {
            invalidInputs.push(emailRef);
        }
        console.log('invalidInputs:', invalidInputs);
        console.log('validInputs:', validInputs);
        return { invalidInputs, validInputs };
    };

    return (
        <div className="mobile-page">
            <InlineModal
                show={customerSupport}
                header={translate('Report an Issue')}
                width="md"
                setShow={setCustomerSupport}
            >
                {customerSupport ? (
                    <SupportForm
                        handleCustomerInputs={handleCustomerInputs}
                        customerSupport={customerSupport}
                        attachedDocuments={attachedDocuments}
                        setAttachedDocuments={setAttachedDocuments}
                        setCustomerSupport={setCustomerSupport}
                        emailRef={emailRef}
                    />
                ) : null}
            </InlineModal>
            <div className="title-with-options display-flex space-between">
                <PageTitle />
            </div>
            <div className="center-text mt-64p">
                <div className="about-logo mr-14p">
                    <Logo />
                </div>
                <br />
                <div className="mb-8p" style={{ fontSize: '13px' }}>
                    {translate(
                        'Struggling or have questions? Our Help Center is here for you! Explore a wide range of topics and find the assistance you need...'
                    )}
                    .
                </div>
                <div
                    onClick={() => {
                        props.history.push('/userManual');
                    }}
                    className="primary-color pointer"
                >
                    <b>{translate('Help Center')}</b>
                </div>
                <div className="mt-32p mb-16p"></div>
                <div className="mb-8p" style={{ fontSize: '13px' }}>
                    {translate('Do you have an issue to report? For a prompt response and assistance')}
                </div>
                <div
                    onClick={() => {
                        setCustomerSupport({
                            facility: '',
                            description: '',
                            email: '',
                            documentUuids: [],
                        });
                    }}
                    className="primary-color pointer"
                >
                    <b>{translate('Report an Issue')}</b>
                </div>
                <div className="mt-32p mb-16p"></div>

                <div className="mb-8p" style={{ fontSize: '13px' }}>
                    {translate('For any further questions or information, feel free to contact us via email')}
                </div>
                <div className="primary-color">info@hologic.io</div>
                <div className="mt-32p mb-16p"></div>

                <div className="mb-8p" style={{ fontSize: '13px' }}>
                    {translate('Current software version')}:
                </div>
                <div className="primary-color">{codeVersion?.version}</div>
                <br />
            </div>
        </div>
    );
}

interface SupportFormProps {
    handleCustomerInputs: Function;
    customerSupport: CustomerSupportForm;
    attachedDocuments: BlobDocumentModel[];
    setAttachedDocuments: Function;
    setCustomerSupport: Function;
    emailRef: any;
}
export const SupportForm = (props: SupportFormProps) => {
    const {
        handleCustomerInputs,
        customerSupport,
        attachedDocuments,
        setAttachedDocuments,
        setCustomerSupport,
        emailRef,
    } = props;
    const [isCardCreated, setIsCardCreated] = React.useState(false as boolean);

    return (
        <div className="">
            {!isCardCreated ? (
                <div>
                    <div className="display-flex">
                        <div className="mr-4p flex">
                            <label className="text-bold ml-4p" htmlFor="">
                                {translate('Name of the facility')}:
                            </label>
                            <input
                                autoComplete="off"
                                name="facility"
                                autoFocus
                                placeholder={translate('Enter name of the facility')}
                                value={customerSupport.facility ? customerSupport.facility : ''}
                                onChange={(e: any) => handleCustomerInputs(e)}
                                type="text"
                                className="input input-modal"
                            />
                        </div>
                    </div>

                    <div className="display-flex mt-12p">
                        <div className="ml-4p flex">
                            <label className="text-bold ml-4p" htmlFor="">
                                {translate('Description')}:
                            </label>
                            <textarea
                                name="description"
                                onChange={(e: any) => handleCustomerInputs(e)}
                                // onBlur={() => setReclamationReason(localReclamationReason)}
                                value={customerSupport.description ? customerSupport.description : ''}
                                className="form-control"
                                placeholder={translate(
                                    'Please describe your problem in as much detail as possible. Our customer support team will immediately start working on the issue and notify you via the email address you provide in the form once the problem is resolved.'
                                )}
                                rows={6}
                            ></textarea>
                        </div>
                    </div>

                    <div className="display-flex mt-12p">
                        <div className="mr-4p flex">
                            <label className="text-bold ml-4p" htmlFor="">
                                {translate('E-mail')}:{' '}
                                <span className="color-secondary">({translate('required')})</span>
                            </label>
                            <input
                                ref={emailRef}
                                autoComplete="off"
                                name="email"
                                placeholder={translate('Enter your email')}
                                value={customerSupport.email ? customerSupport.email : ''}
                                onChange={(e: any) => handleCustomerInputs(e)}
                                type="text"
                                className="input input-modal"
                            />
                        </div>
                    </div>

                    <div className="display-flex mt-16p">
                        <FolderBox
                            selectOptions={undefined}
                            category={'support'}
                            documents={attachedDocuments}
                            setAttachedDocuments={setAttachedDocuments}
                            onFolderUpdated={(documents: any) => {
                                setAttachedDocuments(JSON.parse(documents));
                            }}
                            uploadButtonStyle={'ne'}
                            inputWrapperClass={'input-wrapper'}
                        />
                    </div>
                </div>
            ) : (
                <div>
                    {translate(
                        'The issue has been reported. Customer service will review your issue and begin resolving it.'
                    )}
                </div>
            )}

            <div className="mt-32p flex-center-center">
                {!isCardCreated ? (
                    customerSupport?.email ? (
                        <ConfirmButtons
                            onConfirm={async () => {
                                try {
                                    const newCustomerSupport = { ...customerSupport };
                                    const documentUuids: string[] = [];
                                    attachedDocuments.forEach((ad) => {
                                        if (ad.uuid) {
                                            documentUuids.push(ad.uuid);
                                        }
                                    });
                                    const re =
                                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                                    if (!re.test(String(newCustomerSupport.email).toLowerCase())) {
                                        throw new Error(translate("Email is not valid!"))
                                    }else{
                                        newCustomerSupport.documentUuids = documentUuids;
                                        await createTrelloCard(newCustomerSupport);
                                        setAttachedDocuments([]);
                                        setIsCardCreated(true);
                                    }
                                } catch (error: any) {
                                    toast(error.message, {
                                        type: toast.TYPE.ERROR,
                                    });
                                }
                               
                                
                                // setCustomerSupport(null);
                            }}
                            onCancel={() => {
                                setAttachedDocuments([]);
                                setIsCardCreated(false);

                                setCustomerSupport(null);
                            }}
                        />
                    ) : null
                ) : (
                    <ConfirmButtons
                        confirmText={'Report a new Issue'}
                        cancelText={'Close'}
                        onConfirm={() => {
                            const newCustomerSupport = { ...customerSupport };
                            newCustomerSupport.documentUuids = [];
                            newCustomerSupport.email = '';
                            newCustomerSupport.description = '';
                            setCustomerSupport(newCustomerSupport);
                            setIsCardCreated(false);
                            setAttachedDocuments([]);
                        }}
                        onCancel={() => {
                            setAttachedDocuments([]);
                            setIsCardCreated(false);
                            setCustomerSupport(null);
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default HelpAndSupport;
